import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  SelectChangeEvent,
  IconButton,
  Tooltip,
  Paper,
  Stack,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Pagination,
  Chip,
  Checkbox,
  FormControlLabel,
  Alert,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { useSearchParams } from 'react-router-dom';
import {
  Search as SearchIcon,
  Download as DownloadIcon,
  Visibility as ViewIcon,
  Link as LinkIcon,
  Description as DocumentIcon,
  VideoLibrary as VideoIcon,
  Clear as ClearIcon,
  Add as AddIcon,
  Edit as EditIcon,
  ContentCopy as ContentCopyIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useApiGet } from '../../utils/httpClientTs';
import {
  getDocumentList,
  getCategories,
  getSubCategories,
} from '../../endpoints/documents';
import { DocumentList } from '../../types/document';
import { applications } from '../../utils/variables';
import dayjs from 'dayjs';

const DocumentLibrary: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Load filters from localStorage on component mount, but override with URL params if present
  const [searchTerm, setSearchTerm] = useState(() => {
    const urlSearch = searchParams.get('search');
    return (
      urlSearch || localStorage.getItem('documentLibrary_searchTerm') || ''
    );
  });
  const [delayedSearchTerm, setDelayedSearchTerm] = useState(() => {
    const urlSearch = searchParams.get('search');
    return (
      urlSearch || localStorage.getItem('documentLibrary_searchTerm') || ''
    );
  });
  const [selectedCategories, setSelectedCategories] = useState<string>(() => {
    return localStorage.getItem('documentLibrary_selectedCategories') || '';
  });
  const [selectedSubCategories, setSelectedSubCategories] = useState<string>(
    () => {
      return (
        localStorage.getItem('documentLibrary_selectedSubCategories') || ''
      );
    }
  );
  const [whatsNew, setWhatsNew] = useState(() => {
    return searchParams.get('whats_new') === 'true';
  });
  const [viewMode, setViewMode] = useState<'grid' | 'list'>(() => {
    return (
      (localStorage.getItem('documentLibrary_viewMode') as 'grid' | 'list') ||
      'list'
    );
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [sortBy, setSortBy] = useState<'name' | 'created_at'>(() => {
    const stored = localStorage.getItem('documentLibrary_sortBy');
    return stored === 'name' || stored === 'created_at' ? stored : 'created_at';
  });
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>(() => {
    const stored = localStorage.getItem('documentLibrary_sortOrder');
    return stored === 'asc' || stored === 'desc' ? stored : 'desc';
  });

  // Fetch categories and sub-categories
  const { data: categories = [] } = useApiGet(['categories'], getCategories);
  const { data: subCategories = [] } = useApiGet(
    ['sub-categories'],
    getSubCategories
  );

  // Handle URL search parameter changes (only on initial load or external changes)
  useEffect(() => {
    const urlSearch = searchParams.get('search');
    if (urlSearch !== null && urlSearch !== searchTerm) {
      setSearchTerm(urlSearch);
      setDelayedSearchTerm(urlSearch);
    }
    const urlWhatsNew = searchParams.get('whats_new') === 'true';
    if (urlWhatsNew !== whatsNew) {
      setWhatsNew(urlWhatsNew);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]); // Remove searchTerm from dependencies to prevent loop

  // Update URL params when whats_new changes
  useEffect(() => {
    const newSearchParams = new URLSearchParams(searchParams);
    if (whatsNew) {
      newSearchParams.set('whats_new', 'true');
    } else {
      newSearchParams.delete('whats_new');
    }
    setSearchParams(newSearchParams, { replace: true });
  }, [whatsNew, searchParams, setSearchParams]);

  // Debounced search effect
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDelayedSearchTerm(searchTerm);
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  // Save filters to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('documentLibrary_searchTerm', searchTerm);
  }, [searchTerm]);

  useEffect(() => {
    localStorage.setItem(
      'documentLibrary_selectedCategories',
      selectedCategories
    );
  }, [selectedCategories]);

  useEffect(() => {
    localStorage.setItem(
      'documentLibrary_selectedSubCategories',
      selectedSubCategories
    );
  }, [selectedSubCategories]);

  useEffect(() => {
    localStorage.setItem('documentLibrary_viewMode', viewMode);
  }, [viewMode]);

  useEffect(() => {
    localStorage.setItem('documentLibrary_sortBy', sortBy);
  }, [sortBy]);

  useEffect(() => {
    localStorage.setItem('documentLibrary_sortOrder', sortOrder);
  }, [sortOrder]);

  // Fetch documents with filters
  const {
    data: documentsResponse,
    isLoading,
    refetch,
  } = useApiGet(
    [
      'documents-library',
      delayedSearchTerm || '',
      selectedCategories || 'cat_none',
      selectedSubCategories || 'sub_none',
      whatsNew ? 'whats_new' : 'all',
      sortBy,
      sortOrder,
      currentPage,
      perPage,
    ],
    () =>
      getDocumentList({
        search: delayedSearchTerm || undefined,
        categories: selectedCategories ? [selectedCategories] : undefined,
        sub_categories: selectedSubCategories
          ? [selectedSubCategories]
          : undefined,
        whats_new: whatsNew || undefined,
        sort_by: sortBy,
        order: sortOrder,
        page: currentPage,
        per_page: perPage,
      }),
    { enabled: true }
  );

  // Refresh data when component mounts to ensure fresh data
  useEffect(() => {
    refetch();
  }, [refetch]);

  // Extract documents and pagination info from response
  const documents = documentsResponse?.data || [];
  const pagination = documentsResponse?.pagination;

  const handleCategoryChange = (event: SelectChangeEvent<string>) => {
    const value = event.target.value;
    setSelectedCategories(value);

    // Clear sub-categories if category is changed
    setSelectedSubCategories('');
    // Reset to first page when changing filters
    setCurrentPage(1);
  };

  const handleSubCategoryChange = (event: SelectChangeEvent<string>) => {
    const value = event.target.value;
    setSelectedSubCategories(value);
    // Reset to first page when changing filters
    setCurrentPage(1);
  };

  const handleViewDocument = (document: DocumentList) => {
    if (canCreateDocument()) {
      navigate(`/library/${document.id}/edit`);
    } else {
      navigate(`/library/document/${document.id}/latest`);
    }
  };

  const handleCopyLink = (id: string) => {
    if (id) {
      const link = `${window.location.origin}/library/document/${id}/latest`;
      navigator.clipboard
        .writeText(link)
        .then(() => {
          console.log('Link copied:', link);
        })
        .catch((err) => {
          console.error('Failed to copy link:', err);
          // Fallback: show the link in an alert
          alert(`Link: ${link}`);
        });
    }
  };

  const handleCreateDocument = () => {
    navigate('/library/create');
  };

  const clearFilters = () => {
    setSearchTerm('');
    setDelayedSearchTerm('');
    setSelectedCategories('');
    setSelectedSubCategories('');
    setWhatsNew(false);
    setCurrentPage(1); // Reset to first page when clearing filters

    // Clear localStorage
    localStorage.removeItem('documentLibrary_searchTerm');
    localStorage.removeItem('documentLibrary_selectedCategories');
    localStorage.removeItem('documentLibrary_selectedSubCategories');
  };

  const sortValue =
    sortBy === 'name'
      ? sortOrder === 'asc'
        ? 'name_asc'
        : 'name_desc'
      : sortOrder === 'desc'
        ? 'created_at_desc'
        : 'created_at_asc';

  const handleSortChange = (value: string) => {
    if (value === 'name_asc') {
      setSortBy('name');
      setSortOrder('asc');
    } else if (value === 'name_desc') {
      setSortBy('name');
      setSortOrder('desc');
    } else if (value === 'created_at_asc') {
      setSortBy('created_at');
      setSortOrder('asc');
    } else {
      setSortBy('created_at');
      setSortOrder('desc');
    }
    setCurrentPage(1);
  };

  // Filter sub-categories based on selected categories
  const filteredSubCategories = subCategories.filter(
    (subCat) => selectedCategories === subCat.category_id
  );

  const getFileIcon = (document: DocumentList) => {
    if (document.video_url) {
      return <VideoIcon />;
    } else if (document.link_url) {
      return <LinkIcon />;
    } else if (document.downloadable) {
      return <DownloadIcon />;
    } else {
      return <DocumentIcon />;
    }
  };

  const canCreateDocument = () => {
    // TODO: Update to check for "document:manage" permission in localStorage when permissions are available
    // For now, check the old application-based system
    let apps = localStorage.getItem('apps');
    if (apps) {
      return apps.includes(applications.DocumentAdmin);
    }
    return false;
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" component="h1" sx={{ mb: 1 }}>
          Document Library
        </Typography>
        <Typography variant="subtitle1" component="p" color="text.secondary">
          Find resources tailored to each local chapter officer position,
          information about P.E.O.&apos;s history, ready-to-use chapter programs
          and more. Start with a keyword search, then narrow by Category and
          Sub-Category — keyword search alone often returns too many matches.
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Stack direction="row" spacing={1}>
            {canCreateDocument() && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleCreateDocument}
                sx={{
                  backgroundColor: '#00A859',
                  '&:hover': { backgroundColor: '#008a4a' },
                }}
              >
                Create Document
              </Button>
            )}
            <Button
              variant={viewMode === 'grid' ? 'contained' : 'outlined'}
              onClick={() => setViewMode('grid')}
            >
              Grid
            </Button>
            <Button
              variant={viewMode === 'list' ? 'contained' : 'outlined'}
              onClick={() => setViewMode('list')}
            >
              List
            </Button>
          </Stack>
        </Box>
      </Box>

      {/* Search and Filters */}
      <Paper
        sx={{
          p: 2,
          mb: 3,
          backgroundColor: alpha(theme.palette.primary.main, 0.08),
          border: `1px solid ${alpha(theme.palette.primary.main, 0.16)}`,
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{ mb: 1.5, fontWeight: 700, color: 'primary.main' }}
        >
          Refine results
        </Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              placeholder="Search documents..."
              value={searchTerm}
              size="small"
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'background.paper',
                },
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Category</InputLabel>
              <Select
                value={selectedCategories}
                onChange={handleCategoryChange}
                input={<OutlinedInput label="Category" />}
                renderValue={(selected) => {
                  if (!selected) return 'Select a category';
                  const category = categories.find(
                    (cat) => cat.id === selected
                  );
                  return category?.name || selected;
                }}
                sx={{ backgroundColor: 'background.paper' }}
              >
                <MenuItem value="">
                  <em>All Categories</em>
                </MenuItem>
                {categories.map((category) => (
                  <MenuItem key={category.id} value={category.id}>
                    {category.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          {filteredSubCategories.length > 0 && (
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Sub-Category</InputLabel>
                <Select
                  value={selectedSubCategories}
                  onChange={handleSubCategoryChange}
                  input={<OutlinedInput label="Sub-Category" />}
                  renderValue={(selected) => {
                    if (!selected) return 'Select a sub-category';
                    const subCategory = subCategories.find(
                      (subCat) => subCat.id === selected
                    );
                    return subCategory?.name || selected;
                  }}
                  sx={{ backgroundColor: 'background.paper' }}
                >
                  <MenuItem value="">
                    <em>All Sub-Categories</em>
                  </MenuItem>
                  {filteredSubCategories.map((subCategory) => (
                    <MenuItem key={subCategory.id} value={subCategory.id}>
                      {subCategory.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}
          <Grid item xs={12} md={2}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<ClearIcon />}
              onClick={clearFilters}
              sx={{ backgroundColor: 'background.paper' }}
            >
              Clear
            </Button>
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={whatsNew}
                  onChange={(e) => setWhatsNew(e.target.checked)}
                />
              }
              label="Show only What's New?"
            />
          </Grid>
        </Grid>
      </Paper>

      {/* High-result nudge when no category is selected */}
      {pagination && pagination.total_count > 50 && !selectedCategories && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Showing {pagination.total_count} results — select a Category above to
          narrow your search.
        </Alert>
      )}

      {/* Active Filters Display */}
      {(searchTerm || selectedCategories || selectedSubCategories) && (
        <Paper sx={{ p: 2, mb: 3, backgroundColor: 'action.hover' }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
            Active Filters:
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {searchTerm && (
              <Chip
                label={`Search: "${searchTerm}"`}
                onDelete={() => {
                  setSearchTerm('');
                  setDelayedSearchTerm('');
                }}
                color="primary"
                variant="outlined"
                size="small"
              />
            )}
            {selectedCategories && (
              <Chip
                label={`Category: ${categories.find((cat) => cat.id === selectedCategories)?.name || selectedCategories}`}
                onDelete={() => {
                  setSelectedCategories('');
                  setSelectedSubCategories('');
                }}
                color="secondary"
                variant="outlined"
                size="small"
              />
            )}
            {selectedSubCategories && (
              <Chip
                label={`Sub-category: ${subCategories.find((sub) => sub.id === selectedSubCategories)?.name || selectedSubCategories}`}
                onDelete={() => setSelectedSubCategories('')}
                color="secondary"
                variant="outlined"
                size="small"
              />
            )}
          </Stack>
        </Paper>
      )}

      {/* Results Summary and Sort */}
      <Box
        sx={{
          mb: 2,
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          {pagination
            ? `${pagination.total_count} document${pagination.total_count !== 1 ? 's' : ''} found`
            : `${documents.length} document${documents.length !== 1 ? 's' : ''} found`}
          {searchTerm && ` for "${searchTerm}"`}
          {selectedCategories &&
            ` in ${categories.find((cat) => cat.id === selectedCategories)?.name || selectedCategories}`}
          {pagination &&
            ` (showing ${(pagination.page - 1) * pagination.per_page + 1}-${Math.min(pagination.page * pagination.per_page, pagination.total_count)} of ${pagination.total_count})`}
        </Typography>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel id="document-library-sort-label">Sort by</InputLabel>
          <Select
            labelId="document-library-sort-label"
            value={sortValue}
            label="Sort by"
            onChange={(e) => handleSortChange(e.target.value)}
          >
            <MenuItem value="name_asc">Name (A–Z)</MenuItem>
            <MenuItem value="name_desc">Name (Z–A)</MenuItem>
            <MenuItem value="created_at_desc">Newest first</MenuItem>
            <MenuItem value="created_at_asc">Oldest first</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Pagination Controls */}
      {pagination && pagination.total_pages > 1 && (
        <Box
          sx={{
            mb: 3,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Items per page:
            </Typography>
            <FormControl size="small">
              <Select
                value={perPage}
                onChange={(e) => {
                  setPerPage(Number(e.target.value));
                  setCurrentPage(1); // Reset to first page when changing page size
                }}
                sx={{ minWidth: 80 }}
              >
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={20}>20</MenuItem>
                <MenuItem value={50}>50</MenuItem>
                <MenuItem value={100}>100</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <Pagination
            count={pagination.total_pages}
            page={currentPage}
            onChange={(_, page) => setCurrentPage(page)}
            color="primary"
            showFirstButton
            showLastButton
            size="large"
          />
        </Box>
      )}

      {/* Documents Display */}
      {isLoading ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography>Loading documents...</Typography>
        </Box>
      ) : documents.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No documents found
          </Typography>
          <Typography color="text.secondary">
            {searchTerm || selectedCategories || selectedSubCategories
              ? 'Try adjusting your search criteria or filters.'
              : 'No documents are available in the library.'}
          </Typography>
        </Paper>
      ) : viewMode === 'grid' ? (
        // Grid View
        <Grid container spacing={2}>
          {documents.map((document) => (
            <Grid item xs={12} md={6} lg={4} key={document.id}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <CardContent
                  sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      mb: 2,
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: 'rgba(0, 114, 206, 0.08)',
                        borderRadius: 1,
                        px: 1,
                        py: 0.5,
                      },
                    }}
                    onClick={() => handleViewDocument(document)}
                  >
                    {getFileIcon(document)}
                    <Typography
                      variant="h6"
                      component="h3"
                      sx={{ ml: 1, flexGrow: 1 }}
                    >
                      {document.name}
                    </Typography>
                    {document.whats_new && (
                      <Chip
                        label="New"
                        size="small"
                        sx={{
                          backgroundColor: '#0072CE',
                          color: '#fff',
                          fontWeight: 'bold',
                          ml: 1,
                        }}
                      />
                    )}
                  </Box>

                  <Stack spacing={1} sx={{ mb: 2 }}>
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Version: <strong>{document.version}</strong>
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Created:{' '}
                        <strong>
                          {dayjs(document.created_at).format('MM/DD/YYYY')}
                        </strong>
                      </Typography>
                    </Box>
                  </Stack>

                  <Box sx={{ display: 'flex', gap: 1, mt: 'auto' }}>
                    <Button
                      size="small"
                      variant="outlined"
                      sx={{
                        flexGrow: 1,
                        color: canCreateDocument() ? '#0072CE' : 'inherit',
                        borderColor: canCreateDocument()
                          ? '#0072CE'
                          : 'inherit',
                        '&:hover': {
                          borderColor: canCreateDocument()
                            ? '#005a9e'
                            : 'inherit',
                          backgroundColor: canCreateDocument()
                            ? 'rgba(0, 114, 206, 0.04)'
                            : 'inherit',
                        },
                      }}
                      startIcon={
                        canCreateDocument() ? <EditIcon /> : <ViewIcon />
                      }
                      onClick={() => handleViewDocument(document)}
                    >
                      {canCreateDocument() ? 'Edit' : 'View'}
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : (
        // List View
        <Card>
          <List>
            {documents.map((document, index) => (
              <ListItem
                key={document.id}
                divider={index < documents.length - 1}
                sx={{ py: 2 }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    flexGrow: 1,
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: 'rgba(0, 114, 206, 0.08)',
                      borderRadius: 1,
                      px: 1,
                      py: 0.5,
                    },
                  }}
                  onClick={() => handleViewDocument(document)}
                >
                  <ListItemIcon>{getFileIcon(document)}</ListItemIcon>
                  <ListItemText
                    primary={
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                      >
                        <Typography variant="body1" component="span">
                          {document.name}
                        </Typography>
                        {document.whats_new && (
                          <Chip
                            label="New"
                            size="small"
                            sx={{
                              backgroundColor: '#0072CE',
                              color: '#fff',
                              fontWeight: 'bold',
                            }}
                          />
                        )}
                      </Box>
                    }
                    secondary={
                      <Box>
                        <Typography variant="body2" component="span">
                          Version {document.version} •{' '}
                          {dayjs(document.created_at).format('MM/DD/YYYY')}
                        </Typography>
                      </Box>
                    }
                  />
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Tooltip title={canCreateDocument() ? 'Edit' : 'View'}>
                    <IconButton
                      size="small"
                      sx={{
                        color: canCreateDocument() ? '#0072CE' : 'inherit',
                      }}
                      onClick={() => handleViewDocument(document)}
                    >
                      {canCreateDocument() ? <EditIcon /> : <ViewIcon />}
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Copy Link">
                    <IconButton
                      size="small"
                      sx={{ color: '#6C63FF' }}
                      onClick={() => handleCopyLink(document.id)}
                    >
                      <ContentCopyIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              </ListItem>
            ))}
          </List>
        </Card>
      )}

      {/* Bottom Pagination Controls */}
      {pagination && pagination.total_pages > 1 && (
        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
          <Pagination
            count={pagination.total_pages}
            page={currentPage}
            onChange={(_, page) => setCurrentPage(page)}
            color="primary"
            showFirstButton
            showLastButton
            size="large"
          />
        </Box>
      )}
    </Box>
  );
};

export default DocumentLibrary;
